import { Text } from "@mantine/core";
import { type ReactNode } from "react";

import { StyledCheckbox } from "./CheckboxGroup.styled";
import type { ComboboxItem } from "@mantine/core";

type RecursiveSelectItem = ComboboxItem & {
  parts?: RecursiveSelectItem[];
};

interface Props<K extends RecursiveSelectItem> {
  item: K;
  renderLabel?: (item: K) => ReactNode;
  disabled?: boolean;
  wrapperStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
}

function CheckboxGroupItem<K extends RecursiveSelectItem>({
  item,
  renderLabel,
  disabled,
  wrapperStyles,
  labelStyles,
}: Props<K>) {
  const renderCheckbox = (
    currentItem: K,
    isNested = false,
    visited = new Set<string>(),
  ) => {
    if (visited.has(currentItem.value)) {
      return null;
    }

    const nextVisited = new Set(visited);
    nextVisited.add(currentItem.value);

    return (
      <div
        key={currentItem.value}
        style={isNested ? { paddingLeft: "24px" } : wrapperStyles}>
        <StyledCheckbox
          label={
            renderLabel ? (
              renderLabel(currentItem)
            ) : (
              <Text
                w="100%"
                size="sm"
                style={!isNested ? labelStyles : undefined}>
                {currentItem.label}
              </Text>
            )
          }
          value={currentItem.value}
          labelPosition="left"
          disabled={disabled}
        />

        {currentItem.parts?.map(part =>
          renderCheckbox(part as K, true, nextVisited),
        )}
      </div>
    );
  };

  return renderCheckbox(item, false, new Set<string>());
}

export default CheckboxGroupItem;
